/**
 * Multi-Signature Support for AOS Secure Boot and Module Signing
 *
 * Provides N-of-M threshold signature verification for boot images and modules.
 * Uses Ed25519 signatures with deterministic payload hashing.
 */
import * as ed from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import { sha256 } from '@noble/hashes/sha256';
import { base58 } from './base58.js';
import type { KeyPair } from './keys.js';

// Required for ed25519 in browser/node
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m));

/**
 * Multi-signature policy defining who can sign and threshold requirements
 */
export interface MultiSigPolicy {
  /** Minimum number of valid signatures required */
  threshold: number;
  /** List of allowed signer public keys (base58 encoded) */
  signers: string[];
  /** Optional human-readable name for this policy */
  name?: string;
}

/**
 * Individual signature within a multi-sig envelope
 */
export interface MultiSigSignature {
  /** Signer's public key (base58) */
  signer: string;
  /** Ed25519 signature over the payload hash (base58) */
  signature: string;
  /** Timestamp when signature was created */
  timestamp: number;
}

/**
 * Multi-signature envelope containing payload reference and signatures
 */
export interface MultiSigEnvelope {
  /** SHA-256 hash of the payload being signed (base58) */
  payloadHash: string;
  /** The signing policy for this envelope */
  policy: MultiSigPolicy;
  /** Collection of signatures */
  signatures: MultiSigSignature[];
}

/**
 * Result of multi-sig verification
 */
export interface MultiSigVerifyResult {
  /** Whether the threshold was met with valid signatures */
  valid: boolean;
  /** Number of valid signatures from authorized signers */
  validCount: number;
  /** Required threshold */
  threshold: number;
  /** Details about each signature */
  signatureDetails: {
    signer: string;
    valid: boolean;
    authorized: boolean;
    error?: string;
  }[];
  /** Any errors encountered during verification */
  errors: string[];
}

/**
 * Compute SHA-256 hash of arbitrary data
 */
export function hashPayload(data: Uint8Array): Uint8Array {
  return sha256(data);
}

/**
 * Compute SHA-256 hash of a string (UTF-8 encoded)
 */
export function hashString(str: string): Uint8Array {
  return sha256(new TextEncoder().encode(str));
}

/**
 * Compute SHA-256 hash of a JSON object (canonical form)
 */
export function hashObject(obj: unknown): Uint8Array {
  const canonical = JSON.stringify(obj, Object.keys(obj as object).sort());
  return hashString(canonical);
}

/**
 * Create an empty multi-sig envelope for a payload
 *
 * @param payload - Raw bytes to be signed
 * @param policy - Signing policy (threshold + allowed signers)
 * @returns Empty envelope ready for signatures
 */
export function createMultiSigEnvelope(
  payload: Uint8Array,
  policy: MultiSigPolicy
): MultiSigEnvelope {
  if (policy.threshold < 1) {
    throw new Error('Threshold must be at least 1');
  }
  if (policy.threshold > policy.signers.length) {
    throw new Error('Threshold cannot exceed number of signers');
  }
  if (policy.signers.length === 0) {
    throw new Error('At least one signer required');
  }

  // Check for duplicate signers
  const uniqueSigners = new Set(policy.signers);
  if (uniqueSigners.size !== policy.signers.length) {
    throw new Error('Duplicate signers in policy');
  }

  const payloadHash = base58.encode(hashPayload(payload));

  return {
    payloadHash,
    policy: { ...policy },
    signatures: [],
  };
}

/**
 * Create an envelope from a JSON object (hashes the canonical JSON)
 */
export function createMultiSigEnvelopeFromObject(
  obj: unknown,
  policy: MultiSigPolicy
): MultiSigEnvelope {
  const payload = new TextEncoder().encode(
    JSON.stringify(obj, Object.keys(obj as object).sort())
  );
  return createMultiSigEnvelope(payload, policy);
}

/**
 * Create the signing data for a multi-sig envelope
 * This is what gets signed: "AOSMULTISIG:" + payloadHash
 */
function getSigningData(envelope: MultiSigEnvelope): Uint8Array {
  const prefix = 'AOSMULTISIG:';
  const data = prefix + envelope.payloadHash;
  return new TextEncoder().encode(data);
}

/**
 * Add a signature to a multi-sig envelope
 *
 * @param envelope - Existing envelope (not mutated)
 * @param keyPair - Signer's key pair
 * @returns New envelope with added signature
 */
export function addSignature(
  envelope: MultiSigEnvelope,
  keyPair: KeyPair
): MultiSigEnvelope {
  // Check if signer is authorized
  if (!envelope.policy.signers.includes(keyPair.publicKeyBase58)) {
    throw new Error(`Signer ${keyPair.publicKeyBase58.slice(0, 8)}... not in policy`);
  }

  // Check if already signed by this key
  if (envelope.signatures.some(s => s.signer === keyPair.publicKeyBase58)) {
    throw new Error(`Already signed by ${keyPair.publicKeyBase58.slice(0, 8)}...`);
  }

  // Create signature
  const signingData = getSigningData(envelope);
  const signatureBytes = ed.sign(signingData, keyPair.privateKey);

  const newSignature: MultiSigSignature = {
    signer: keyPair.publicKeyBase58,
    signature: base58.encode(signatureBytes),
    timestamp: Date.now(),
  };

  return {
    ...envelope,
    signatures: [...envelope.signatures, newSignature],
  };
}

/**
 * Verify a single signature
 */
function verifySingleSignature(
  envelope: MultiSigEnvelope,
  sig: MultiSigSignature
): { valid: boolean; error?: string } {
  try {
    const signingData = getSigningData(envelope);
    const signatureBytes = base58.decode(sig.signature);
    const publicKeyBytes = base58.decode(sig.signer);

    const valid = ed.verify(signatureBytes, signingData, publicKeyBytes);
    return { valid };
  } catch (err) {
    return {
      valid: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Verify a multi-sig envelope against its policy
 *
 * @param envelope - Envelope to verify
 * @param revokedKeys - Optional set of revoked public keys to reject
 * @returns Verification result with details
 */
export function verifyMultiSig(
  envelope: MultiSigEnvelope,
  revokedKeys?: Set<string>
): MultiSigVerifyResult {
  const errors: string[] = [];
  const signatureDetails: MultiSigVerifyResult['signatureDetails'] = [];
  let validCount = 0;

  // Validate policy
  if (envelope.policy.threshold < 1) {
    errors.push('Invalid policy: threshold must be at least 1');
  }
  if (envelope.policy.threshold > envelope.policy.signers.length) {
    errors.push('Invalid policy: threshold exceeds signer count');
  }

  // Check each signature
  for (const sig of envelope.signatures) {
    const authorized = envelope.policy.signers.includes(sig.signer);
    const revoked = revokedKeys?.has(sig.signer) ?? false;

    if (revoked) {
      signatureDetails.push({
        signer: sig.signer,
        valid: false,
        authorized,
        error: 'Key has been revoked',
      });
      errors.push(`Signature from revoked key: ${sig.signer.slice(0, 8)}...`);
      continue;
    }

    if (!authorized) {
      signatureDetails.push({
        signer: sig.signer,
        valid: false,
        authorized: false,
        error: 'Signer not in policy',
      });
      errors.push(`Unauthorized signer: ${sig.signer.slice(0, 8)}...`);
      continue;
    }

    const result = verifySingleSignature(envelope, sig);

    signatureDetails.push({
      signer: sig.signer,
      valid: result.valid,
      authorized: true,
      error: result.error,
    });

    if (result.valid) {
      validCount++;
    } else {
      errors.push(`Invalid signature from ${sig.signer.slice(0, 8)}...: ${result.error || 'verification failed'}`);
    }
  }

  const valid = validCount >= envelope.policy.threshold && errors.length === 0;

  return {
    valid,
    validCount,
    threshold: envelope.policy.threshold,
    signatureDetails,
    errors,
  };
}

/**
 * Check if an envelope has enough signatures (without full verification)
 */
export function hasEnoughSignatures(envelope: MultiSigEnvelope): boolean {
  return envelope.signatures.length >= envelope.policy.threshold;
}

/**
 * Get list of signers who haven't signed yet
 */
export function getPendingSigners(envelope: MultiSigEnvelope): string[] {
  const signed = new Set(envelope.signatures.map(s => s.signer));
  return envelope.policy.signers.filter(s => !signed.has(s));
}

/**
 * Serialize an envelope to JSON (for storage/transmission)
 */
export function serializeEnvelope(envelope: MultiSigEnvelope): string {
  return JSON.stringify(envelope);
}

/**
 * Deserialize an envelope from JSON
 */
export function deserializeEnvelope(json: string): MultiSigEnvelope {
  const parsed = JSON.parse(json);

  // Validate structure
  if (!parsed.payloadHash || typeof parsed.payloadHash !== 'string') {
    throw new Error('Invalid envelope: missing payloadHash');
  }
  if (!parsed.policy || typeof parsed.policy !== 'object') {
    throw new Error('Invalid envelope: missing policy');
  }
  if (typeof parsed.policy.threshold !== 'number') {
    throw new Error('Invalid envelope: missing policy.threshold');
  }
  if (!Array.isArray(parsed.policy.signers)) {
    throw new Error('Invalid envelope: missing policy.signers');
  }
  if (!Array.isArray(parsed.signatures)) {
    throw new Error('Invalid envelope: missing signatures');
  }

  return parsed as MultiSigEnvelope;
}

/**
 * Create a policy from a list of public keys with a threshold
 */
export function createPolicy(
  signers: string[],
  threshold: number,
  name?: string
): MultiSigPolicy {
  if (threshold < 1) {
    throw new Error('Threshold must be at least 1');
  }
  if (threshold > signers.length) {
    throw new Error('Threshold cannot exceed number of signers');
  }
  if (signers.length === 0) {
    throw new Error('At least one signer required');
  }

  return {
    threshold,
    signers: [...signers],
    name,
  };
}

/**
 * Check if a policy is satisfied by a set of public keys
 */
export function policySatisfiedBy(
  policy: MultiSigPolicy,
  pubkeys: string[]
): boolean {
  const validSigners = pubkeys.filter(pk => policy.signers.includes(pk));
  return validSigners.length >= policy.threshold;
}
